import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { gsap } from '@/lib/gsap'
import { cn } from '@/lib/utils'
import { useLenis } from '@/components/motion/smooth-scroll'
import { usePageTransition } from '@/components/motion/transition-context'
import RollText from '@/components/motion/roll-text'
import LiveClock from '@/components/generals/live-clock'
import MobileMenu from '@/components/generals/mobile-menu'
import { navLinks, stores } from '@/constants/site'

const Header: FC = (): ReactNode => {
    const router = useRouter()
    const lenis = useLenis()
    const { navigating } = usePageTransition()
    const bar = useRef<HTMLElement>(null)
    const hidden = useRef(false)
    const [menuOpen, setMenuOpen] = useState(false)

    const isActive = (href: string): boolean => router.pathname === href || router.pathname.startsWith(`${href}/`)

    useEffect(() => {
        if (!lenis) return

        const setHidden = (value: boolean): void => {
            if (hidden.current === value || !bar.current) return

            hidden.current = value
            gsap.to(bar.current, { yPercent: value ? -100 : 0, duration: 0.7, ease: 'expo.out', overwrite: 'auto' })
        }

        return lenis.on('scroll', instance => {
            if (menuOpen) return setHidden(false)
            if (instance.scroll < 140) return setHidden(false)
            if (instance.direction === 1) setHidden(true)
            if (instance.direction === -1) setHidden(false)
        })
    }, [lenis, menuOpen])

    useEffect(() => {
        if (!navigating || !bar.current) return

        hidden.current = false
        gsap.set(bar.current, { yPercent: 0 })
        setMenuOpen(false)
    }, [navigating])

    return (
        <>
            <header
                ref={bar}
                className='fixed inset-x-0 top-0 z-50 border-b border-line bg-ink/85 backdrop-blur-md will-change-transform'
            >
                <div className='relative flex h-[var(--header-height)] items-center justify-between gutter'>
                    <Link
                        href='/'
                        aria-label='Elio, home'
                        className='group flex items-center gap-2.5'
                    >
                        <span className='font-heading text-[28px] leading-none tracking-[0.01em] text-bone transition-colors duration-500 group-hover:text-acid'>
                            ELIO
                        </span>

                        <span className='ping' aria-hidden='true' />
                    </Link>

                    <nav aria-label='Main' className='absolute left-1/2 hidden -translate-x-1/2 lg:block'>
                        <ul className='flex items-center gap-9'>
                            {navLinks.map(link => (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        aria-current={isActive(link.href) ? 'page' : undefined}
                                        className={cn(
                                            'roll-host relative flex items-center gap-2 label-mono transition-colors duration-300 hover:text-acid',
                                            isActive(link.href) ? 'text-acid' : 'text-bone'
                                        )}
                                    >
                                        <span
                                            aria-hidden='true'
                                            className={cn(
                                                'size-1 rounded-full bg-acid transition-transform duration-500 ease-expo-out',
                                                isActive(link.href) ? 'scale-100' : 'scale-0'
                                            )}
                                        />

                                        <RollText text={link.label} />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='flex items-center gap-6'>
                        <p className='hidden items-center gap-2 meta-mono text-ash md:flex'>
                            <span className='ping' aria-hidden='true' />

                            <span>
                                {stores[0].short}
                            </span>

                            <LiveClock timeZone={stores[0].timeZone} />
                        </p>

                        <Link
                            href='/shop'
                            className='roll-host hidden label-mono text-bone transition-colors duration-300 hover:text-acid lg:inline-flex'
                        >
                            <RollText text='Shop now' />
                        </Link>

                        <button
                            type='button'
                            aria-expanded={menuOpen}
                            aria-controls='mobile-menu'
                            onClick={() => setMenuOpen(open => !open)}
                            className='flex h-11 items-center gap-3 label-mono text-bone lg:hidden'
                        >
                            <span>
                                {menuOpen ? 'Close' : 'Menu'}
                            </span>

                            <span aria-hidden='true' className='relative block h-3 w-6'>
                                <span
                                    className={cn(
                                        'absolute left-0 top-0.5 h-px w-full bg-current transition-transform duration-500 ease-expo-out',
                                        menuOpen && 'translate-y-[4.5px] rotate-45'
                                    )}
                                />

                                <span
                                    className={cn(
                                        'absolute bottom-0.5 left-0 h-px w-full bg-current transition-transform duration-500 ease-expo-out',
                                        menuOpen && '-translate-y-[4.5px] -rotate-45'
                                    )}
                                />
                            </span>
                        </button>
                    </div>
                </div>
            </header>

            <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
        </>
    )
}

export default Header
